// The answer-payload vocabulary: every `type` tag a chat-timeline SSE frame may
// carry, and a gate that says whether a decoded frame is one of them.
//
// This is a VOCABULARY gate, not a field validator. The transport parses each
// frame as JSON and forwards it on a bare cast; what it could not say was
// whether the tag it just forwarded is one anything downstream handles. A
// backend that ships a new tag before the frontend learns it used to reach the
// reducer silently and fall out of every branch. So the gate answers one
// question — is this tag in the vocabulary — and answers it without touching
// the payload: `decodeAnswerPayload` returns the ARGUMENT, never a copy, so the
// reducer keeps seeing the same object and no branch changes behaviour.
//
// Zero imports on purpose. A discriminant check needs no schema runtime, and
// this door is inert (`manifest-boundary.test.ts`).
//
// The list is not hand-maintained prose: `answerPayload.test.ts` reads the
// reducer (`processChatChunk.ts`) and the transport (`timelineEventSource.ts`)
// off disk and asserts this array EQUALS the union of the tags they branch on,
// so a tag added to either without being added here fails there.

/** Every `type` tag the reducer or the transport branches on. */
export const ANSWER_PAYLOAD_TYPES = [
  'appointments',
  'calculator_result',
  'capability_tool',
  'capacity',
  'chat_metadata',
  'clinical_task_result',
  'create_template',
  'csv_export',
  'drug_referenced',
  'edit_template_section',
  'email_patient_evidence',
  'excel_export',
  'form_fill',
  'get_template_and_preferences',
  'heartbeat',
  'interactive_widget',
  'library_search_call',
  'medical_calculator',
  'message_info',
  'patient_evidence_pdf',
  'patient_summary',
  'pdf_export',
  'pptx_export',
  'print_document',
  'processing',
  'quota_exhausted',
  'reasoning',
  'search_community_templates',
  'search_metadata',
  'search_my_templates',
  'search_patient_profiles',
  'search_templates',
  'sessions_by_date',
  'stream_complete',
  'text',
  'thinking',
  'tier_restricted_tool_error',
  'unauthenticated_tool_access_error',
  'visualdx',
  'web_search_call',
  'word_export',
] as const;

export type AnswerPayloadType = (typeof ANSWER_PAYLOAD_TYPES)[number];

export interface DecodedAnswerPayload<T> {
  /** Whether `type` is a member of `ANSWER_PAYLOAD_TYPES`. */
  known: boolean;
  /** The frame's discriminant, or `undefined` when absent or not a string. */
  type: string | undefined;
  /** The argument itself — never a copy, so downstream identity is preserved. */
  payload: T;
}

const KNOWN_TYPES: ReadonlySet<string> = new Set<string>(ANSWER_PAYLOAD_TYPES);

/**
 * Reads a decoded SSE frame's discriminant and reports whether it is one the
 * timeline vocabulary knows. Never throws and never rewrites: an unknown or
 * absent tag is reported, not rejected, so the caller decides what to do.
 */
export const decodeAnswerPayload = <T extends { type?: unknown }>(
  raw: T
): DecodedAnswerPayload<T> => {
  const type = typeof raw?.type === 'string' ? raw.type : undefined;
  return {
    known: type !== undefined && KNOWN_TYPES.has(type),
    type,
    payload: raw,
  };
};
